/**
 * NativeVoiceFactory - Native Web Audio API Voice Factory
 * 
 * Creates voice instances using native Web Audio API nodes (OscillatorNode, GainNode, etc.)
 * Replaces Tone.js synthesizers with native implementations.
 */

import { ADSREnvelope, ADSRSettings } from './ADSREnvelope';
import type { InstrumentType } from '../../types/composition';
import type { InstrumentParameters, FilterSettings, OscillatorSettings } from '../../types/instruments';

/**
 * Voice interface for native Web Audio API voices
 */
export interface Voice {
  /** The voice's gain node for volume control */
  gainNode: GainNode;
  
  /** Optional effects node (AudioWorkletNode) for WASM effects */
  effectsNode: AudioWorkletNode | null;
  
  /** Optional filter node for frequency filtering */
  filterNode: BiquadFilterNode | null;
  
  /** Trigger a note at a specific time */
  trigger: (note: string, time: number, duration: number) => void;
  
  /** Update voice parameters in real-time */
  updateParameters: (parameters: InstrumentParameters) => void;
  
  /** Dispose of the voice and clean up resources */
  dispose: () => void;
}

/**
 * Configuration for creating a voice
 */
export interface VoiceConfig {
  /** Native AudioContext */
  audioContext: AudioContext;
  
  /** Instrument type to create */
  instrumentType: InstrumentType;
  
  /** ADSR envelope settings */
  adsr?: ADSRSettings | undefined;
  
  /** Instrument parameters */
  parameters?: InstrumentParameters | undefined;
  
  /** Optional effects node to connect to */
  effectsNode?: AudioWorkletNode | null | undefined;
  
  /** Destination node (usually master gain or effects) */
  destination: AudioNode;
}

/**
 * Default ADSR settings for different instrument types
 */
const DEFAULT_ADSR: ADSRSettings = {
  attack: 0.01,
  decay: 0.1,
  sustain: 0.7,
  release: 0.3,
};

/**
 * Create and configure a BiquadFilterNode based on filter settings
 * 
 * @param audioContext - Native AudioContext
 * @param filterSettings - Filter configuration
 * @returns Configured BiquadFilterNode or null if no filter settings
 */
function createFilterNode(
  audioContext: AudioContext,
  filterSettings?: FilterSettings
): BiquadFilterNode | null {
  if (!filterSettings) {
    return null;
  }

  const filter = audioContext.createBiquadFilter();
  
  // Set filter type (default to lowpass)
  filter.type = filterSettings.type || 'lowpass';
  
  // Set frequency (default to 20000 Hz for lowpass, 20 Hz for highpass, 1000 Hz for bandpass)
  if (filterSettings.frequency !== undefined) {
    // Clamp frequency to valid range (20 Hz - 20000 Hz)
    filter.frequency.value = Math.max(20, Math.min(20000, filterSettings.frequency));
  } else {
    // Set sensible defaults based on filter type
    switch (filter.type) {
      case 'lowpass':
        filter.frequency.value = 20000; // Fully open by default
        break;
      case 'highpass':
        filter.frequency.value = 20; // Fully open by default
        break;
      case 'bandpass':
        filter.frequency.value = 1000; // Middle frequency
        break;
    }
  }
  
  // Set resonance/Q (default to 1.0)
  if (filterSettings.resonance !== undefined) {
    filter.Q.value = filterSettings.resonance;
  } else {
    filter.Q.value = 1.0;
  }
  
  return filter;
}

/**
 * Update filter parameters in real-time
 * 
 * @param filter - BiquadFilterNode to update
 * @param filterSettings - New filter settings
 */
function updateFilterParameters(
  filter: BiquadFilterNode,
  filterSettings: FilterSettings
): void {
  // Update filter type if specified
  if (filterSettings.type !== undefined) {
    filter.type = filterSettings.type;
  }
  
  // Update frequency if specified (with clamping)
  if (filterSettings.frequency !== undefined) {
    const clampedFreq = Math.max(20, Math.min(20000, filterSettings.frequency));
    filter.frequency.setValueAtTime(clampedFreq, filter.context.currentTime);
  }
  
  // Update resonance/Q if specified
  if (filterSettings.resonance !== undefined) {
    filter.Q.setValueAtTime(filterSettings.resonance, filter.context.currentTime);
  }
}

/**
 * Update oscillator parameters in real-time for active oscillators
 * 
 * @param oscillators - Array of active OscillatorNodes
 * @param audioContext - AudioContext for timing
 * @param oscillatorSettings - New oscillator settings
 */
function updateOscillatorParameters(
  oscillators: OscillatorNode[],
  audioContext: AudioContext,
  oscillatorSettings: OscillatorSettings
): void {
  const currentTime = audioContext.currentTime;
  
  oscillators.forEach(oscillator => {
    // Update detune if specified
    if (oscillatorSettings.detune !== undefined) {
      oscillator.detune.setValueAtTime(oscillatorSettings.detune, currentTime);
    }
    
    // Note: oscillator type and frequency cannot be changed after creation
    // These changes will only apply to newly triggered notes
    // Frequency is determined by the note being played, not by parameters
  });
}

/**
 * Create a custom PeriodicWave for pulse waves with specified duty cycle
 * Uses Fourier series to generate pulse waveforms
 * 
 * @param audioContext - Native AudioContext
 * @param dutyCycle - Duty cycle (0.0 to 1.0), e.g., 0.25 for 25% pulse
 * @returns PeriodicWave for use with OscillatorNode
 */
function createPulseWave(audioContext: AudioContext, dutyCycle: number): PeriodicWave {
  // Number of harmonics to generate (more = better quality but more CPU)
  const harmonics = 32;
  
  // Create arrays for real and imaginary components
  // Length must be harmonics + 1 (includes DC component at index 0)
  const real = new Float32Array(harmonics + 1);
  const imag = new Float32Array(harmonics + 1);
  
  // DC component (index 0) represents the average value
  // For a pulse wave, this is: 2 * dutyCycle - 1
  real[0] = 2 * dutyCycle - 1;
  imag[0] = 0;
  
  // Generate harmonics using Fourier series for pulse wave
  // Formula: a_n = (2/n*pi) * sin(n * pi * dutyCycle)
  for (let n = 1; n <= harmonics; n++) {
    const coefficient = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * dutyCycle);
    real[n] = coefficient;
    imag[n] = 0; // Pulse waves have no imaginary component
  }
  
  // Create and return the PeriodicWave
  return audioContext.createPeriodicWave(real, imag, { disableNormalization: false });
}

/**
 * Convert note name to frequency in Hz
 * Supports notes like 'C4', 'D#5', 'F3', etc.
 */
function noteToFrequency(note: string): number {
  const noteMap: Record<string, number> = {
    'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
    'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
    'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11,
  };

  // Parse note name and octave
  const match = note.match(/^([A-G][#b]?)(\d+)$/);
  if (!match) {
    console.warn(`Invalid note format: ${note}, defaulting to C4`);
    return 261.63; // C4
  }

  const noteName = match[1];
  const octaveStr = match[2];
  
  if (!noteName || !octaveStr) {
    console.warn(`Invalid note format: ${note}, defaulting to C4`);
    return 261.63; // C4
  }
  
  const octave = parseInt(octaveStr, 10);
  const noteIndex = noteMap[noteName];

  if (noteIndex === undefined) {
    console.warn(`Unknown note: ${noteName}, defaulting to C4`);
    return 261.63; // C4
  }

  // Calculate frequency using A4 = 440 Hz as reference
  // Formula: f = 440 * 2^((n - 69) / 12)
  // where n is the MIDI note number
  const midiNote = (octave + 1) * 12 + noteIndex;
  const frequency = 440 * Math.pow(2, (midiNote - 69) / 12);

  return frequency;
}

/**
 * Create a pulse wave voice (pulse25, pulse50, pulse75)
 */
function createPulseVoice(config: VoiceConfig): Voice {
  const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
  
  // Determine duty cycle based on instrument type
  let dutyCycle: number;
  switch (instrumentType) {
    case 'pulse25':
      dutyCycle = 0.25;
      break;
    case 'pulse50':
      dutyCycle = 0.50;
      break;
    case 'pulse75':
      dutyCycle = 0.75;
      break;
    default:
      dutyCycle = 0.50; // Default to 50% if unknown
  }

  // Create custom PeriodicWave for this duty cycle
  const pulseWave = createPulseWave(audioContext, dutyCycle);

  // Use provided ADSR or default
  const adsrSettings = adsr || DEFAULT_ADSR;

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5; // Default volume
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active oscillators for cleanup
  let activeOscillators: OscillatorNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      const frequency = noteToFrequency(note);

      // Create oscillator
      const oscillator = audioContext.createOscillator();
      
      // Apply custom pulse wave using setPeriodicWave()
      oscillator.setPeriodicWave(pulseWave);
      
      oscillator.frequency.value = frequency;

      // Apply detune if specified in parameters
      if (parameters?.oscillator?.detune) {
        oscillator.detune.value = parameters.oscillator.detune;
      }

      // Connect oscillator through envelope
      oscillator.connect(envelope.getGainNode());

      // Start oscillator at scheduled time
      oscillator.start(time);

      // Stop oscillator after note duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      oscillator.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeOscillators.push(oscillator);

      // Clean up after oscillator stops
      oscillator.onended = () => {
        try {
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected oscillators
        }
        activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update oscillator parameters for active oscillators
      if (newParameters.oscillator) {
        updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active oscillators
      activeOscillators.forEach(oscillator => {
        try {
          oscillator.stop();
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-stopped oscillators
        }
      });
      activeOscillators = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Generate white noise buffer
 * White noise has equal energy at all frequencies
 */
function generateWhiteNoise(audioContext: AudioContext, duration: number = 2.0): AudioBuffer {
  const sampleRate = audioContext.sampleRate;
  const bufferSize = sampleRate * duration;
  const buffer = audioContext.createBuffer(1, bufferSize, sampleRate);
  const data = buffer.getChannelData(0);

  // Generate random values between -1 and 1
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }

  return buffer;
}

/**
 * Generate pink noise buffer
 * Pink noise has equal energy per octave (1/f spectrum)
 * Uses the Voss-McCartney algorithm
 */
function generatePinkNoise(audioContext: AudioContext, duration: number = 2.0): AudioBuffer {
  const sampleRate = audioContext.sampleRate;
  const bufferSize = sampleRate * duration;
  const buffer = audioContext.createBuffer(1, bufferSize, sampleRate);
  const data = buffer.getChannelData(0);

  // Voss-McCartney algorithm with 16 generators
  const numGenerators = 16;
  const generators = new Array(numGenerators).fill(0);
  let maxKey = 0x1f; // 31 in binary: 11111
  let key = 0;

  for (let i = 0; i < bufferSize; i++) {
    // Determine which generators to update
    const lastKey = key;
    key++;
    if (key > maxKey) key = 0;

    // XOR to find which bits changed
    const diff = lastKey ^ key;

    // Update generators where bits changed
    let sum = 0;
    for (let j = 0; j < numGenerators; j++) {
      if (diff & (1 << j)) {
        generators[j] = Math.random() * 2 - 1;
      }
      sum += generators[j];
    }

    // Average and normalize
    data[i] = sum / numGenerators;
  }

  // Normalize to -1 to 1 range
  let max = 0;
  for (let i = 0; i < bufferSize; i++) {
    const value = data[i];
    if (value !== undefined) {
      max = Math.max(max, Math.abs(value));
    }
  }
  if (max > 0) {
    for (let i = 0; i < bufferSize; i++) {
      const value = data[i];
      if (value !== undefined) {
        data[i] = value / max;
      }
    }
  }

  return buffer;
}

/**
 * Generate brown noise buffer
 * Brown noise has 1/f^2 spectrum (more bass-heavy than pink)
 * Uses random walk algorithm
 */
function generateBrownNoise(audioContext: AudioContext, duration: number = 2.0): AudioBuffer {
  const sampleRate = audioContext.sampleRate;
  const bufferSize = sampleRate * duration;
  const buffer = audioContext.createBuffer(1, bufferSize, sampleRate);
  const data = buffer.getChannelData(0);

  // Random walk algorithm
  let lastValue = 0;
  for (let i = 0; i < bufferSize; i++) {
    // Add small random step
    const step = (Math.random() * 2 - 1) * 0.02;
    lastValue += step;

    // Clamp to prevent drift
    if (lastValue > 1.0) lastValue = 1.0;
    if (lastValue < -1.0) lastValue = -1.0;

    data[i] = lastValue;
  }

  // Normalize to -1 to 1 range
  let max = 0;
  for (let i = 0; i < bufferSize; i++) {
    const value = data[i];
    if (value !== undefined) {
      max = Math.max(max, Math.abs(value));
    }
  }
  if (max > 0) {
    for (let i = 0; i < bufferSize; i++) {
      const value = data[i];
      if (value !== undefined) {
        data[i] = value / max;
      }
    }
  }

  return buffer;
}

/**
 * Create a noise voice (white, pink, brown)
 */
function createNoiseVoice(config: VoiceConfig): Voice {
  const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
  
  // Generate appropriate noise buffer based on type
  let noiseBuffer: AudioBuffer;
  switch (instrumentType) {
    case 'noise-white':
      noiseBuffer = generateWhiteNoise(audioContext);
      break;
    case 'noise-pink':
      noiseBuffer = generatePinkNoise(audioContext);
      break;
    case 'noise-brown':
      noiseBuffer = generateBrownNoise(audioContext);
      break;
    default:
      // Default to white noise
      noiseBuffer = generateWhiteNoise(audioContext);
  }

  // Use provided ADSR or default
  const adsrSettings = adsr || DEFAULT_ADSR;

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.3; // Lower volume for noise (it's loud!)
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active noise sources for cleanup
  let activeNoiseSources: AudioBufferSourceNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      // Create buffer source for noise playback
      const noiseSource = audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      noiseSource.loop = true; // Loop the noise buffer

      // Optional: Apply pitch shift based on note
      // Noise doesn't have a fundamental frequency, but we can shift it
      const frequency = noteToFrequency(note);
      const baseFrequency = 261.63; // C4
      const pitchRatio = frequency / baseFrequency;
      noiseSource.playbackRate.value = pitchRatio;

      // Connect noise source through envelope
      noiseSource.connect(envelope.getGainNode());

      // Start noise at scheduled time
      noiseSource.start(time);

      // Stop noise after note duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      noiseSource.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeNoiseSources.push(noiseSource);

      // Clean up after noise stops
      noiseSource.onended = () => {
        try {
          noiseSource.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected sources
        }
        activeNoiseSources = activeNoiseSources.filter(src => src !== noiseSource);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active noise sources
      activeNoiseSources.forEach(source => {
        try {
          source.stop();
          source.disconnect();
        } catch (e) {
          // Ignore errors from already-stopped sources
        }
      });
      activeNoiseSources = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Create an FM synthesis voice
 * FM (Frequency Modulation) synthesis uses a modulator oscillator to modulate
 * the frequency of a carrier oscillator, creating complex harmonic content
 */
function createFMVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, parameters, effectsNode, destination } = config;
  
  // Use provided ADSR or default
  const adsrSettings = adsr || DEFAULT_ADSR;

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5; // Default volume
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active oscillators for cleanup
  let activeOscillators: OscillatorNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      const frequency = noteToFrequency(note);

      // FM synthesis parameters
      const modulationDepth = parameters?.modulation?.depth ?? 100; // Hz
      const modulationFrequency = parameters?.modulation?.frequency ?? 2; // Ratio to carrier

      // Create modulator oscillator
      const modulator = audioContext.createOscillator();
      modulator.type = 'sine'; // Modulator is typically sine wave
      modulator.frequency.value = frequency * modulationFrequency;

      // Create modulation depth gain (controls how much modulation affects carrier)
      const modulationGain = audioContext.createGain();
      modulationGain.gain.value = modulationDepth;

      // Create carrier oscillator
      const carrier = audioContext.createOscillator();
      carrier.type = 'sine'; // Carrier can be any waveform
      carrier.frequency.value = frequency;

      // Connect modulator to carrier frequency (FM synthesis)
      modulator.connect(modulationGain);
      modulationGain.connect(carrier.frequency);

      // Connect carrier through envelope
      carrier.connect(envelope.getGainNode());

      // Start both oscillators at scheduled time
      modulator.start(time);
      carrier.start(time);

      // Stop oscillators after note duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      modulator.stop(stopTime);
      carrier.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeOscillators.push(modulator, carrier);
      activeGains.push(modulationGain);

      // Clean up after oscillators stop
      carrier.onended = () => {
        try {
          modulator.disconnect();
          modulationGain.disconnect();
          carrier.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
        activeOscillators = activeOscillators.filter(osc => osc !== modulator && osc !== carrier);
        activeGains = activeGains.filter(gain => gain !== modulationGain);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update oscillator parameters for active oscillators
      if (newParameters.oscillator) {
        updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
      }
      
      // Update modulation parameters for active gains
      if (newParameters.modulation) {
        const currentTime = audioContext.currentTime;
        activeGains.forEach(gain => {
          if (newParameters.modulation?.depth !== undefined) {
            gain.gain.setValueAtTime(newParameters.modulation.depth, currentTime);
          }
        });
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active oscillators
      activeOscillators.forEach(oscillator => {
        try {
          oscillator.stop();
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-stopped oscillators
        }
      });
      activeOscillators = [];

      // Disconnect all gains
      activeGains.forEach(gain => {
        try {
          gain.disconnect();
        } catch (e) {
          // Ignore errors
        }
      });
      activeGains = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Create an AM synthesis voice
 * AM (Amplitude Modulation) synthesis uses a modulator oscillator to modulate
 * the amplitude (volume) of a carrier oscillator, creating tremolo and ring modulation effects
 */
function createAMVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, parameters, effectsNode, destination } = config;
  
  // Use provided ADSR or default
  const adsrSettings = adsr || DEFAULT_ADSR;

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5; // Default volume
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active oscillators for cleanup
  let activeOscillators: OscillatorNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      const frequency = noteToFrequency(note);

      // AM synthesis parameters
      const modulationDepth = parameters?.modulation?.depth ?? 0.5; // 0.0 to 1.0
      const modulationFrequency = parameters?.modulation?.frequency ?? 5; // Hz

      // Create carrier oscillator
      const carrier = audioContext.createOscillator();
      carrier.type = 'sine'; // Carrier waveform
      carrier.frequency.value = frequency;

      // Create modulation gain (this will be modulated by the modulator)
      const modulationGain = audioContext.createGain();
      // Set DC offset so modulation oscillates around 1.0
      // Formula: gain = 1.0 + (modulator * depth)
      // We need to offset by (1.0 - depth/2) so it oscillates between (1-depth) and 1
      modulationGain.gain.value = 1.0 - (modulationDepth / 2);

      // Create modulator oscillator
      const modulator = audioContext.createOscillator();
      modulator.type = 'sine'; // Modulator is typically sine wave
      modulator.frequency.value = modulationFrequency;

      // Create gain for modulator depth control
      const depthGain = audioContext.createGain();
      depthGain.gain.value = modulationDepth / 2; // Scale modulator output

      // Connect AM synthesis chain:
      // Carrier -> ModulationGain -> Envelope
      // Modulator -> DepthGain -> ModulationGain.gain (modulates the gain)
      carrier.connect(modulationGain);
      modulationGain.connect(envelope.getGainNode());
      
      modulator.connect(depthGain);
      depthGain.connect(modulationGain.gain);

      // Start both oscillators at scheduled time
      carrier.start(time);
      modulator.start(time);

      // Stop oscillators after note duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      carrier.stop(stopTime);
      modulator.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeOscillators.push(carrier, modulator);
      activeGains.push(modulationGain, depthGain);

      // Clean up after oscillators stop
      carrier.onended = () => {
        try {
          carrier.disconnect();
          modulator.disconnect();
          modulationGain.disconnect();
          depthGain.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
        activeOscillators = activeOscillators.filter(osc => osc !== carrier && osc !== modulator);
        activeGains = activeGains.filter(gain => gain !== modulationGain && gain !== depthGain);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update oscillator parameters for active oscillators
      if (newParameters.oscillator) {
        updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
      }
      
      // Update modulation parameters for active gains
      if (newParameters.modulation) {
        const currentTime = audioContext.currentTime;
        const newDepth = newParameters.modulation.depth;
        const newFreq = newParameters.modulation.frequency;
        
        // Update depth gain and modulation gain offset
        if (newDepth !== undefined) {
          activeGains.forEach((gain, index) => {
            if (index % 2 === 0) {
              // modulationGain (even indices)
              gain.gain.setValueAtTime(1.0 - (newDepth / 2), currentTime);
            } else {
              // depthGain (odd indices)
              gain.gain.setValueAtTime(newDepth / 2, currentTime);
            }
          });
        }
        
        // Update modulator frequency
        if (newFreq !== undefined) {
          activeOscillators.forEach((osc, index) => {
            if (index % 2 === 1) {
              // Modulator oscillators (odd indices)
              osc.frequency.setValueAtTime(newFreq, currentTime);
            }
          });
        }
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active oscillators
      activeOscillators.forEach(oscillator => {
        try {
          oscillator.stop();
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-stopped oscillators
        }
      });
      activeOscillators = [];

      // Disconnect all gains
      activeGains.forEach(gain => {
        try {
          gain.disconnect();
        } catch (e) {
          // Ignore errors
        }
      });
      activeGains = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Create a basic oscillator voice (square, triangle, sawtooth, sine)
 */
function createOscillatorVoice(config: VoiceConfig): Voice {
  const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
  
  // Determine oscillator type
  let oscillatorType: OscillatorType;
  const basicTypes: Record<string, OscillatorType> = {
    'square': 'square',
    'triangle': 'triangle',
    'sawtooth': 'sawtooth',
    'sine': 'sine',
  };
  
  oscillatorType = basicTypes[instrumentType] || 'sine';

  // Use provided ADSR or default
  const adsrSettings = adsr || DEFAULT_ADSR;

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5; // Default volume
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  // Connect to effects or destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active oscillators for cleanup
  let activeOscillators: OscillatorNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      const frequency = noteToFrequency(note);

      // Create oscillator
      const oscillator = audioContext.createOscillator();
      oscillator.type = oscillatorType;
      oscillator.frequency.value = frequency;

      // Apply detune if specified in parameters
      if (parameters?.oscillator?.detune) {
        oscillator.detune.value = parameters.oscillator.detune;
      }

      // Connect oscillator through envelope
      oscillator.connect(envelope.getGainNode());

      // Start oscillator at scheduled time
      oscillator.start(time);

      // Stop oscillator after note duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      oscillator.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeOscillators.push(oscillator);

      // Clean up after oscillator stops
      oscillator.onended = () => {
        try {
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected oscillators
        }
        activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update oscillator parameters for active oscillators
      if (newParameters.oscillator) {
        updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active oscillators
      activeOscillators.forEach(oscillator => {
        try {
          oscillator.stop();
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-stopped oscillators
        }
      });
      activeOscillators = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Create a kick drum voice
 * Kick drum: low frequency oscillator + pitch envelope + noise burst
 */
function createKickDrumVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Kick drum has a very short, punchy envelope
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.15,
    sustain: 0.0,
    release: 0.05,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.8; // Kick is loud
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeOscillators: OscillatorNode[] = [];
  let activeNoiseSources: AudioBufferSourceNode[] = [];
  let activeFilters: BiquadFilterNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null, // Drum voices use internal filters for sound design

    trigger(_note: string, time: number, duration: number): void {
      // Kick drum components:
      // 1. Low frequency oscillator with pitch envelope (main body)
      // 2. Short noise burst (attack transient)

      // Create main oscillator (sine wave for body)
      const oscillator = audioContext.createOscillator();
      oscillator.type = 'sine';
      
      // Start at higher frequency and sweep down (pitch envelope)
      const startFreq = 150; // Start frequency
      const endFreq = 40;    // End frequency (low bass)
      const pitchDecay = 0.05; // How long the pitch sweep takes
      
      oscillator.frequency.setValueAtTime(startFreq, time);
      oscillator.frequency.exponentialRampToValueAtTime(endFreq, time + pitchDecay);

      // Connect oscillator through envelope
      oscillator.connect(envelope.getGainNode());

      // Create noise burst for attack transient
      const noiseBuffer = generateWhiteNoise(audioContext, 0.1);
      const noiseSource = audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      // Filter noise to emphasize low frequencies
      const noiseFilter = audioContext.createBiquadFilter();
      noiseFilter.type = 'lowpass';
      noiseFilter.frequency.value = 200;
      noiseFilter.Q.value = 1;

      // Noise gain (very short burst)
      const noiseGain = audioContext.createGain();
      noiseGain.gain.setValueAtTime(0.3, time);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, time + 0.03);

      // Connect noise: source -> filter -> gain -> envelope
      noiseSource.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(envelope.getGainNode());

      // Start both sources
      oscillator.start(time);
      noiseSource.start(time);

      // Stop after duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      oscillator.stop(stopTime);
      noiseSource.stop(Math.min(time + 0.05, stopTime)); // Noise stops quickly

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeOscillators.push(oscillator);
      activeNoiseSources.push(noiseSource);
      activeFilters.push(noiseFilter);
      activeGains.push(noiseGain);

      // Clean up after oscillator stops
      oscillator.onended = () => {
        try {
          oscillator.disconnect();
          noiseSource.disconnect();
          noiseFilter.disconnect();
          noiseGain.disconnect();
        } catch (e) {
          // Ignore errors
        }
        activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
        activeNoiseSources = activeNoiseSources.filter(src => src !== noiseSource);
        activeFilters = activeFilters.filter(f => f !== noiseFilter);
        activeGains = activeGains.filter(g => g !== noiseGain);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Drum voices don't support real-time parameter updates for oscillators
      // Parameters will be applied on next trigger
    },

    dispose(): void {
      // Stop all active nodes
      activeOscillators.forEach(osc => {
        try { osc.stop(); osc.disconnect(); } catch (e) { /* ignore */ }
      });
      activeNoiseSources.forEach(src => {
        try { src.stop(); src.disconnect(); } catch (e) { /* ignore */ }
      });
      activeFilters.forEach(f => {
        try { f.disconnect(); } catch (e) { /* ignore */ }
      });
      activeGains.forEach(g => {
        try { g.disconnect(); } catch (e) { /* ignore */ }
      });
      
      activeOscillators = [];
      activeNoiseSources = [];
      activeFilters = [];
      activeGains = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Create a snare drum voice
 * Snare drum: noise + resonant filter + short envelope
 */
function createSnareDrumVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Snare has a short, snappy envelope
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.1,
    sustain: 0.0,
    release: 0.05,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.6;
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeOscillators: OscillatorNode[] = [];
  let activeNoiseSources: AudioBufferSourceNode[] = [];
  let activeFilters: BiquadFilterNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null, // Drum voices use internal filters for sound design

    trigger(_note: string, time: number, duration: number): void {
      // Snare drum components:
      // 1. Oscillator for body (low frequency)
      // 2. Filtered noise for snare wires

      // Create body oscillator (triangle wave)
      const bodyOsc = audioContext.createOscillator();
      bodyOsc.type = 'triangle';
      bodyOsc.frequency.setValueAtTime(180, time);
      bodyOsc.frequency.exponentialRampToValueAtTime(100, time + 0.05);

      const bodyGain = audioContext.createGain();
      bodyGain.gain.setValueAtTime(0.4, time);
      bodyGain.gain.exponentialRampToValueAtTime(0.01, time + 0.08);

      bodyOsc.connect(bodyGain);
      bodyGain.connect(envelope.getGainNode());

      // Create noise for snare wires
      const noiseBuffer = generateWhiteNoise(audioContext, 0.2);
      const noiseSource = audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      // Resonant bandpass filter for snare character
      const noiseFilter = audioContext.createBiquadFilter();
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = 3000; // Mid-high frequencies
      noiseFilter.Q.value = 5; // High resonance for snare character

      // Connect noise: source -> filter -> envelope
      noiseSource.connect(noiseFilter);
      noiseFilter.connect(envelope.getGainNode());

      // Start both sources
      bodyOsc.start(time);
      noiseSource.start(time);

      // Stop after duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      bodyOsc.stop(stopTime);
      noiseSource.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeOscillators.push(bodyOsc);
      activeNoiseSources.push(noiseSource);
      activeFilters.push(noiseFilter);
      activeGains.push(bodyGain);

      // Clean up after oscillator stops
      bodyOsc.onended = () => {
        try {
          bodyOsc.disconnect();
          bodyGain.disconnect();
          noiseSource.disconnect();
          noiseFilter.disconnect();
        } catch (e) {
          // Ignore errors
        }
        activeOscillators = activeOscillators.filter(osc => osc !== bodyOsc);
        activeNoiseSources = activeNoiseSources.filter(src => src !== noiseSource);
        activeFilters = activeFilters.filter(f => f !== noiseFilter);
        activeGains = activeGains.filter(g => g !== bodyGain);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      Object.assign({}, newParameters);
    },

    dispose(): void {
      activeOscillators.forEach(osc => {
        try { osc.stop(); osc.disconnect(); } catch (e) { /* ignore */ }
      });
      activeNoiseSources.forEach(src => {
        try { src.stop(); src.disconnect(); } catch (e) { /* ignore */ }
      });
      activeFilters.forEach(f => {
        try { f.disconnect(); } catch (e) { /* ignore */ }
      });
      activeGains.forEach(g => {
        try { g.disconnect(); } catch (e) { /* ignore */ }
      });
      
      activeOscillators = [];
      activeNoiseSources = [];
      activeFilters = [];
      activeGains = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Create a hi-hat voice
 * Hi-hat: high-pass filtered noise + very short envelope
 */
function createHiHatVoice(config: VoiceConfig, isOpen: boolean = false): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Hi-hat has a very short envelope (closed) or slightly longer (open)
  const adsrSettings = adsr || (isOpen ? {
    attack: 0.001,
    decay: 0.2,
    sustain: 0.0,
    release: 0.1,
  } : {
    attack: 0.001,
    decay: 0.05,
    sustain: 0.0,
    release: 0.02,
  });

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.4;
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeNoiseSources: AudioBufferSourceNode[] = [];
  let activeFilters: BiquadFilterNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null, // Drum voices use internal filters for sound design

    trigger(_note: string, time: number, duration: number): void {
      // Hi-hat: high-pass filtered noise

      // Create noise source
      const noiseBuffer = generateWhiteNoise(audioContext, 0.2);
      const noiseSource = audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      // High-pass filter for metallic character
      const noiseFilter = audioContext.createBiquadFilter();
      noiseFilter.type = 'highpass';
      noiseFilter.frequency.value = 7000; // High frequencies only
      noiseFilter.Q.value = 1;

      // Optional: add bandpass for more metallic character
      const metallicFilter = audioContext.createBiquadFilter();
      metallicFilter.type = 'bandpass';
      metallicFilter.frequency.value = 10000;
      metallicFilter.Q.value = 0.5;

      // Connect: noise -> highpass -> bandpass -> envelope
      noiseSource.connect(noiseFilter);
      noiseFilter.connect(metallicFilter);
      metallicFilter.connect(envelope.getGainNode());

      // Start noise
      noiseSource.start(time);

      // Stop after duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      noiseSource.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeNoiseSources.push(noiseSource);
      activeFilters.push(noiseFilter, metallicFilter);

      // Clean up after noise stops
      noiseSource.onended = () => {
        try {
          noiseSource.disconnect();
          noiseFilter.disconnect();
          metallicFilter.disconnect();
        } catch (e) {
          // Ignore errors
        }
        activeNoiseSources = activeNoiseSources.filter(src => src !== noiseSource);
        activeFilters = activeFilters.filter(f => f !== noiseFilter && f !== metallicFilter);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      Object.assign({}, newParameters);
    },

    dispose(): void {
      activeNoiseSources.forEach(src => {
        try { src.stop(); src.disconnect(); } catch (e) { /* ignore */ }
      });
      activeFilters.forEach(f => {
        try { f.disconnect(); } catch (e) { /* ignore */ }
      });
      
      activeNoiseSources = [];
      activeFilters = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Create a tom drum voice
 * Tom drum: tuned oscillator + pitch envelope
 */
function createTomDrumVoice(config: VoiceConfig, pitch: 'low' | 'mid' | 'high' = 'mid'): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Tom has a medium-length envelope
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.2,
    sustain: 0.0,
    release: 0.1,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.7;
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeOscillators: OscillatorNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null, // Drum voices use internal filters for sound design

    trigger(_note: string, time: number, duration: number): void {
      // Tom drum: tuned oscillator with pitch envelope

      // Determine frequency based on tom type
      let startFreq: number;
      let endFreq: number;
      
      switch (pitch) {
        case 'low':
          startFreq = 120;
          endFreq = 60;
          break;
        case 'high':
          startFreq = 250;
          endFreq = 150;
          break;
        case 'mid':
        default:
          startFreq = 180;
          endFreq = 100;
          break;
      }

      // Create oscillator (sine wave for body)
      const oscillator = audioContext.createOscillator();
      oscillator.type = 'sine';
      
      // Pitch envelope (sweep down)
      const pitchDecay = 0.08;
      oscillator.frequency.setValueAtTime(startFreq, time);
      oscillator.frequency.exponentialRampToValueAtTime(endFreq, time + pitchDecay);

      // Connect oscillator through envelope
      oscillator.connect(envelope.getGainNode());

      // Start oscillator
      oscillator.start(time);

      // Stop after duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      oscillator.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeOscillators.push(oscillator);

      // Clean up after oscillator stops
      oscillator.onended = () => {
        try {
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors
        }
        activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      Object.assign({}, newParameters);
    },

    dispose(): void {
      activeOscillators.forEach(osc => {
        try { osc.stop(); osc.disconnect(); } catch (e) { /* ignore */ }
      });
      activeOscillators = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Create a cymbal voice (crash or ride)
 * Cymbal: multiple detuned oscillators + high-pass filtered noise
 */
function createCymbalVoice(config: VoiceConfig, type: 'crash' | 'ride' = 'crash'): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Crash has longer decay, ride is shorter
  const adsrSettings = adsr || (type === 'crash' ? {
    attack: 0.001,
    decay: 0.5,
    sustain: 0.0,
    release: 0.3,
  } : {
    attack: 0.001,
    decay: 0.15,
    sustain: 0.0,
    release: 0.1,
  });

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5;
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeOscillators: OscillatorNode[] = [];
  let activeNoiseSources: AudioBufferSourceNode[] = [];
  let activeFilters: BiquadFilterNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null, // Drum voices use internal filters for sound design

    trigger(_note: string, time: number, duration: number): void {
      // Cymbal: multiple detuned oscillators + filtered noise

      // Create multiple detuned square wave oscillators for metallic character
      const frequencies = [3140, 4100, 5800, 7200, 9000]; // Inharmonic frequencies
      
      frequencies.forEach(freq => {
        const osc = audioContext.createOscillator();
        osc.type = 'square';
        osc.frequency.value = freq;

        const oscGain = audioContext.createGain();
        oscGain.gain.value = 0.1 / frequencies.length; // Divide by number of oscillators

        osc.connect(oscGain);
        oscGain.connect(envelope.getGainNode());

        osc.start(time);
        const stopTime = time + duration + adsrSettings.release + 0.1;
        osc.stop(stopTime);

        activeOscillators.push(osc);
        activeGains.push(oscGain);
      });

      // Add high-pass filtered noise
      const noiseBuffer = generateWhiteNoise(audioContext, 0.5);
      const noiseSource = audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      const noiseFilter = audioContext.createBiquadFilter();
      noiseFilter.type = 'highpass';
      noiseFilter.frequency.value = 5000;
      noiseFilter.Q.value = 1;

      const noiseGain = audioContext.createGain();
      noiseGain.gain.value = 0.3;

      noiseSource.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(envelope.getGainNode());

      noiseSource.start(time);
      const stopTime = time + duration + adsrSettings.release + 0.1;
      noiseSource.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeNoiseSources.push(noiseSource);
      activeFilters.push(noiseFilter);
      activeGains.push(noiseGain);

      // Clean up after first oscillator stops
      if (activeOscillators.length > 0) {
        const firstOsc = activeOscillators[0];
        if (firstOsc) {
          firstOsc.onended = () => {
            try {
              activeOscillators.forEach(osc => osc.disconnect());
              activeGains.forEach(g => g.disconnect());
              noiseSource.disconnect();
              noiseFilter.disconnect();
            } catch (e) {
              // Ignore errors
            }
            activeOscillators = [];
            activeGains = [];
            activeNoiseSources = [];
            activeFilters = [];
          };
        }
      }
    },

    updateParameters(newParameters: InstrumentParameters): void {
      Object.assign({}, newParameters);
    },

    dispose(): void {
      activeOscillators.forEach(osc => {
        try { osc.stop(); osc.disconnect(); } catch (e) { /* ignore */ }
      });
      activeNoiseSources.forEach(src => {
        try { src.stop(); src.disconnect(); } catch (e) { /* ignore */ }
      });
      activeFilters.forEach(f => {
        try { f.disconnect(); } catch (e) { /* ignore */ }
      });
      activeGains.forEach(g => {
        try { g.disconnect(); } catch (e) { /* ignore */ }
      });
      
      activeOscillators = [];
      activeNoiseSources = [];
      activeFilters = [];
      activeGains = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Create a clap voice
 * Clap: filtered noise bursts with slight delay
 */
function createClapVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Clap has a short, sharp envelope
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.08,
    sustain: 0.0,
    release: 0.03,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.6;
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeNoiseSources: AudioBufferSourceNode[] = [];
  let activeFilters: BiquadFilterNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null, // Drum voices use internal filters for sound design

    trigger(_note: string, time: number, duration: number): void {
      // Clap: multiple noise bursts with slight delays to simulate multiple hands

      const delays = [0, 0.01, 0.02, 0.03]; // Slight delays between bursts
      
      delays.forEach(delay => {
        const noiseBuffer = generateWhiteNoise(audioContext, 0.1);
        const noiseSource = audioContext.createBufferSource();
        noiseSource.buffer = noiseBuffer;

        // Bandpass filter for clap character
        const noiseFilter = audioContext.createBiquadFilter();
        noiseFilter.type = 'bandpass';
        noiseFilter.frequency.value = 1500;
        noiseFilter.Q.value = 2;

        noiseSource.connect(noiseFilter);
        noiseFilter.connect(envelope.getGainNode());

        noiseSource.start(time + delay);
        const stopTime = time + delay + 0.05;
        noiseSource.stop(stopTime);

        activeNoiseSources.push(noiseSource);
        activeFilters.push(noiseFilter);
      });

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Clean up after last noise burst stops
      const lastSource = activeNoiseSources[activeNoiseSources.length - 1];
      if (lastSource) {
        lastSource.onended = () => {
          try {
            activeNoiseSources.forEach(src => src.disconnect());
            activeFilters.forEach(f => f.disconnect());
          } catch (e) {
            // Ignore errors
          }
          activeNoiseSources = [];
          activeFilters = [];
        };
      }
    },

    updateParameters(newParameters: InstrumentParameters): void {
      Object.assign({}, newParameters);
    },

    dispose(): void {
      activeNoiseSources.forEach(src => {
        try { src.stop(); src.disconnect(); } catch (e) { /* ignore */ }
      });
      activeFilters.forEach(f => {
        try { f.disconnect(); } catch (e) { /* ignore */ }
      });
      
      activeNoiseSources = [];
      activeFilters = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Create a pluck voice using Karplus-Strong synthesis
 * Karplus-Strong: DelayNode + BiquadFilterNode feedback loop initialized with noise burst
 */
function createPluckVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, parameters, effectsNode, destination } = config;
  
  // Pluck has a natural decay, so we use a simple envelope
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.1,
    sustain: 0.3,
    release: 0.5,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.6;
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeNoiseSources: AudioBufferSourceNode[] = [];
  let activeDelays: DelayNode[] = [];
  let activeFilters: BiquadFilterNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      const frequency = noteToFrequency(note);
      
      // Karplus-Strong algorithm:
      // 1. Create a delay line with delay time = 1/frequency
      // 2. Initialize with a short noise burst
      // 3. Feed the output back through a lowpass filter to the input
      // 4. The feedback creates a decaying pitched sound

      // Calculate delay time (period of the note)
      const delayTime = 1 / frequency;

      // Create delay node
      const delay = audioContext.createDelay(5.0); // Max delay of 5 seconds
      delay.delayTime.value = delayTime;

      // Create feedback filter (lowpass to simulate string damping)
      const feedbackFilter = audioContext.createBiquadFilter();
      feedbackFilter.type = 'lowpass';
      feedbackFilter.frequency.value = frequency * 4; // Filter cutoff above fundamental
      feedbackFilter.Q.value = 1;

      // Create feedback gain (controls decay rate, must be < 1.0 for stability)
      const feedbackGain = audioContext.createGain();
      feedbackGain.gain.value = 0.95; // High feedback for longer sustain

      // Create the feedback loop: delay -> filter -> feedbackGain -> delay
      delay.connect(feedbackFilter);
      feedbackFilter.connect(feedbackGain);
      feedbackGain.connect(delay);

      // Connect delay output to envelope
      delay.connect(envelope.getGainNode());

      // Create noise burst to initialize the delay line
      const noiseDuration = 0.01; // Very short burst
      const noiseBuffer = generateWhiteNoise(audioContext, noiseDuration);
      const noiseSource = audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      // Connect noise to delay line to start the oscillation
      noiseSource.connect(delay);

      // Start noise burst at scheduled time
      noiseSource.start(time);
      noiseSource.stop(time + noiseDuration);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeNoiseSources.push(noiseSource);
      activeDelays.push(delay);
      activeFilters.push(feedbackFilter);
      activeGains.push(feedbackGain);

      // Schedule cleanup after note ends
      const stopTime = time + duration + adsrSettings.release + 0.5;
      
      // Gradually reduce feedback to stop oscillation
      feedbackGain.gain.setValueAtTime(0.95, time + duration);
      feedbackGain.gain.linearRampToValueAtTime(0, stopTime);

      // Clean up after delay stops
      setTimeout(() => {
        try {
          noiseSource.disconnect();
          delay.disconnect();
          feedbackFilter.disconnect();
          feedbackGain.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
        activeNoiseSources = activeNoiseSources.filter(src => src !== noiseSource);
        activeDelays = activeDelays.filter(d => d !== delay);
        activeFilters = activeFilters.filter(f => f !== feedbackFilter);
        activeGains = activeGains.filter(g => g !== feedbackGain);
      }, (stopTime - audioContext.currentTime) * 1000 + 100);
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active noise sources
      activeNoiseSources.forEach(src => {
        try { src.stop(); src.disconnect(); } catch (e) { /* ignore */ }
      });
      
      // Disconnect all nodes
      activeDelays.forEach(d => {
        try { d.disconnect(); } catch (e) { /* ignore */ }
      });
      activeFilters.forEach(f => {
        try { f.disconnect(); } catch (e) { /* ignore */ }
      });
      activeGains.forEach(g => {
        try { g.disconnect(); } catch (e) { /* ignore */ }
      });
      
      activeNoiseSources = [];
      activeDelays = [];
      activeFilters = [];
      activeGains = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Create a metal voice using multiple detuned oscillators
 * Metal synthesis: 5-7 detuned oscillators with inharmonic frequencies for metallic percussive sound
 */
function createMetalVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, parameters, effectsNode, destination } = config;
  
  // Metal has a short, percussive envelope
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.3,
    sustain: 0.0,
    release: 0.2,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5;
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active oscillators for cleanup
  let activeOscillators: OscillatorNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      const frequency = noteToFrequency(note);

      // Metal synthesis: multiple detuned oscillators with inharmonic ratios
      // These ratios create a metallic, bell-like timbre
      const inharmonicRatios = [1.0, 2.13, 3.17, 4.42, 5.63, 6.89, 8.12];
      
      // Create oscillators for each inharmonic partial
      inharmonicRatios.forEach((ratio, index) => {
        const oscillator = audioContext.createOscillator();
        oscillator.type = 'sine'; // Use sine waves for clean partials
        oscillator.frequency.value = frequency * ratio;

        // Apply slight random detuning for more complex sound
        const randomDetune = (Math.random() - 0.5) * 10; // ±5 cents
        oscillator.detune.value = randomDetune;

        // Create gain for this oscillator (higher partials are quieter)
        const oscGain = audioContext.createGain();
        const amplitude = 1.0 / (index + 1); // Decrease amplitude for higher partials
        oscGain.gain.value = amplitude / inharmonicRatios.length;

        // Connect: oscillator -> gain -> envelope
        oscillator.connect(oscGain);
        oscGain.connect(envelope.getGainNode());

        // Start oscillator at scheduled time
        oscillator.start(time);

        // Stop oscillator after note duration + release
        const stopTime = time + duration + adsrSettings.release + 0.1;
        oscillator.stop(stopTime);

        // Store for cleanup
        activeOscillators.push(oscillator);
        activeGains.push(oscGain);

        // Clean up after oscillator stops
        oscillator.onended = () => {
          try {
            oscillator.disconnect();
            oscGain.disconnect();
          } catch (e) {
            // Ignore errors from already-disconnected oscillators
          }
          activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
          activeGains = activeGains.filter(g => g !== oscGain);
        };
      });

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update oscillator parameters for active oscillators
      if (newParameters.oscillator) {
        updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active oscillators
      activeOscillators.forEach(oscillator => {
        try {
          oscillator.stop();
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-stopped oscillators
        }
      });
      activeOscillators = [];

      // Disconnect all gains
      activeGains.forEach(gain => {
        try {
          gain.disconnect();
        } catch (e) {
          // Ignore errors
        }
      });
      activeGains = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Create a pad voice with long ADSR envelope and rich oscillator combinations
 * Pad instruments have slow attack and long release for atmospheric sounds
 */
function createPadVoice(config: VoiceConfig): Voice {
  const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
  
  // Pad instruments have long attack and release for atmospheric sounds
  const adsrSettings = adsr || {
    attack: 0.8,    // Slow fade-in
    decay: 0.3,
    sustain: 0.7,
    release: 1.5,   // Long fade-out
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.4; // Lower volume for pads (they're layered)
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active oscillators for cleanup
  let activeOscillators: OscillatorNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      const frequency = noteToFrequency(note);

      // Create rich pad sound with multiple detuned oscillators
      // Different pad types use different oscillator combinations
      let oscillatorConfigs: Array<{ type: OscillatorType; detune: number; gain: number }> = [];

      switch (instrumentType) {
        case 'pad-choir':
          // Choir-like sound: multiple sawtooth waves with slight detuning
          oscillatorConfigs = [
            { type: 'sawtooth', detune: -10, gain: 0.25 },
            { type: 'sawtooth', detune: -5, gain: 0.25 },
            { type: 'sawtooth', detune: 5, gain: 0.25 },
            { type: 'sawtooth', detune: 10, gain: 0.25 },
          ];
          break;
        case 'pad-sweep':
          // Sweeping sound: triangle and sine waves with wider detuning
          oscillatorConfigs = [
            { type: 'triangle', detune: -15, gain: 0.3 },
            { type: 'sine', detune: 0, gain: 0.4 },
            { type: 'triangle', detune: 15, gain: 0.3 },
          ];
          break;
        case 'pad-drone':
          // Drone sound: square waves with harmonic detuning
          oscillatorConfigs = [
            { type: 'square', detune: 0, gain: 0.3 },
            { type: 'square', detune: 1200, gain: 0.2 }, // +1 octave
            { type: 'square', detune: 1900, gain: 0.15 }, // +perfect fifth above octave
          ];
          break;
        default:
          // Default pad: sawtooth waves
          oscillatorConfigs = [
            { type: 'sawtooth', detune: -7, gain: 0.33 },
            { type: 'sawtooth', detune: 0, gain: 0.34 },
            { type: 'sawtooth', detune: 7, gain: 0.33 },
          ];
      }

      // Create oscillators based on configuration
      oscillatorConfigs.forEach(config => {
        const oscillator = audioContext.createOscillator();
        oscillator.type = config.type;
        oscillator.frequency.value = frequency;
        oscillator.detune.value = config.detune;

        // Apply additional detune from parameters if specified
        if (parameters?.oscillator?.detune) {
          oscillator.detune.value += parameters.oscillator.detune;
        }

        // Create gain for this oscillator
        const oscGain = audioContext.createGain();
        oscGain.gain.value = config.gain;

        // Connect: oscillator -> gain -> envelope
        oscillator.connect(oscGain);
        oscGain.connect(envelope.getGainNode());

        // Start oscillator at scheduled time
        oscillator.start(time);

        // Stop oscillator after note duration + release
        const stopTime = time + duration + adsrSettings.release + 0.1;
        oscillator.stop(stopTime);

        // Store for cleanup
        activeOscillators.push(oscillator);
        activeGains.push(oscGain);

        // Clean up after oscillator stops
        oscillator.onended = () => {
          try {
            oscillator.disconnect();
            oscGain.disconnect();
          } catch (e) {
            // Ignore errors from already-disconnected oscillators
          }
          activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
          activeGains = activeGains.filter(g => g !== oscGain);
        };
      });

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update oscillator parameters for active oscillators
      if (newParameters.oscillator) {
        updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active oscillators
      activeOscillators.forEach(oscillator => {
        try {
          oscillator.stop();
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-stopped oscillators
        }
      });
      activeOscillators = [];

      // Disconnect all gains
      activeGains.forEach(gain => {
        try {
          gain.disconnect();
        } catch (e) {
          // Ignore errors
        }
      });
      activeGains = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Create an arp (arpeggiator) voice with short ADSR envelope for plucky sounds
 * Arp instruments have fast attack and short release for rhythmic patterns
 */
function createArpVoice(config: VoiceConfig): Voice {
  const { audioContext, instrumentType, adsr, parameters, effectsNode, destination } = config;
  
  // Arp instruments have short, plucky envelopes
  const adsrSettings = adsr || {
    attack: 0.01,   // Fast attack
    decay: 0.1,
    sustain: 0.3,
    release: 0.1,   // Short release
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5;
  
  // Create filter node if filter settings are provided
  const filterNode = createFilterNode(audioContext, parameters?.filter);
  
  // Build audio graph: envelope -> filter (optional) -> effects (optional) -> destination
  if (filterNode) {
    if (effectsNode) {
      voiceGain.connect(filterNode);
      filterNode.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(filterNode);
      filterNode.connect(destination);
    }
  } else {
    if (effectsNode) {
      voiceGain.connect(effectsNode);
      effectsNode.connect(destination);
    } else {
      voiceGain.connect(destination);
    }
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active oscillators for cleanup
  let activeOscillators: OscillatorNode[] = [];
  let activeGains: GainNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: filterNode,

    trigger(note: string, time: number, duration: number): void {
      const frequency = noteToFrequency(note);

      // Create arp sound based on type
      // Different arp types use different oscillator configurations
      let oscillatorConfigs: Array<{ type: OscillatorType; detune: number; gain: number }> = [];

      switch (instrumentType) {
        case 'arp-fast':
          // Fast arp: bright square wave with slight detuning for width
          oscillatorConfigs = [
            { type: 'square', detune: -5, gain: 0.5 },
            { type: 'square', detune: 5, gain: 0.5 },
          ];
          break;
        case 'arp-slow':
          // Slow arp: warmer sawtooth with subtle detuning
          oscillatorConfigs = [
            { type: 'sawtooth', detune: -3, gain: 0.5 },
            { type: 'sawtooth', detune: 3, gain: 0.5 },
          ];
          break;
        case 'arp-random':
          // Random arp: triangle wave with wider detuning for unpredictability
          oscillatorConfigs = [
            { type: 'triangle', detune: -10, gain: 0.4 },
            { type: 'triangle', detune: 0, gain: 0.3 },
            { type: 'triangle', detune: 10, gain: 0.3 },
          ];
          break;
        case 'arp-chord':
          // Chord arp: multiple oscillators tuned to chord intervals
          oscillatorConfigs = [
            { type: 'sawtooth', detune: 0, gain: 0.35 },      // Root
            { type: 'sawtooth', detune: 400, gain: 0.3 },     // Major third
            { type: 'sawtooth', detune: 700, gain: 0.35 },    // Perfect fifth
          ];
          break;
        default:
          // Default arp: square wave
          oscillatorConfigs = [
            { type: 'square', detune: 0, gain: 1.0 },
          ];
      }

      // Create oscillators based on configuration
      oscillatorConfigs.forEach(config => {
        const oscillator = audioContext.createOscillator();
        oscillator.type = config.type;
        oscillator.frequency.value = frequency;
        oscillator.detune.value = config.detune;

        // Apply additional detune from parameters if specified
        if (parameters?.oscillator?.detune) {
          oscillator.detune.value += parameters.oscillator.detune;
        }

        // Create gain for this oscillator
        const oscGain = audioContext.createGain();
        oscGain.gain.value = config.gain;

        // Connect: oscillator -> gain -> envelope
        oscillator.connect(oscGain);
        oscGain.connect(envelope.getGainNode());

        // Start oscillator at scheduled time
        oscillator.start(time);

        // Stop oscillator after note duration + release
        const stopTime = time + duration + adsrSettings.release + 0.1;
        oscillator.stop(stopTime);

        // Store for cleanup
        activeOscillators.push(oscillator);
        activeGains.push(oscGain);

        // Clean up after oscillator stops
        oscillator.onended = () => {
          try {
            oscillator.disconnect();
            oscGain.disconnect();
          } catch (e) {
            // Ignore errors from already-disconnected oscillators
          }
          activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
          activeGains = activeGains.filter(g => g !== oscGain);
        };
      });

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);
    },

    updateParameters(newParameters: InstrumentParameters): void {
      // Update filter parameters in real-time
      if (filterNode && newParameters.filter) {
        updateFilterParameters(filterNode, newParameters.filter);
      }
      
      // Update oscillator parameters for active oscillators
      if (newParameters.oscillator) {
        updateOscillatorParameters(activeOscillators, audioContext, newParameters.oscillator);
      }
      
      // Update ADSR settings for next trigger
      if (newParameters.adsr) {
        Object.assign(adsrSettings, newParameters.adsr);
      }
      
      // Store all parameters for next trigger
      if (parameters) {
        Object.assign(parameters, newParameters);
      }
    },

    dispose(): void {
      // Stop all active oscillators
      activeOscillators.forEach(oscillator => {
        try {
          oscillator.stop();
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors from already-stopped oscillators
        }
      });
      activeOscillators = [];

      // Disconnect all gains
      activeGains.forEach(gain => {
        try {
          gain.disconnect();
        } catch (e) {
          // Ignore errors
        }
      });
      activeGains = [];

      // Dispose envelope
      envelope.dispose();

      // Disconnect filter if present
      if (filterNode) {
        try {
          filterNode.disconnect();
        } catch (e) {
          // Ignore errors from already-disconnected nodes
        }
      }

      // Disconnect voice gain
      try {
        voiceGain.disconnect();
      } catch (e) {
        // Ignore errors from already-disconnected nodes
      }
    },
  };

  return voice;
}

/**
 * Create a laser SFX voice
 * Laser: frequency sweep + oscillator with parameter automation
 */
function createLaserVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Laser has a very short envelope
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.15,
    sustain: 0.0,
    release: 0.05,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5;
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeOscillators: OscillatorNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null,

    trigger(_note: string, time: number, duration: number): void {
      // Laser sound: frequency sweep from high to low

      // Create oscillator (square or sawtooth for laser character)
      const oscillator = audioContext.createOscillator();
      oscillator.type = 'sawtooth';
      
      // Frequency sweep parameters
      const startFreq = 1200; // Start high
      const endFreq = 200;    // End low
      const sweepDuration = Math.min(duration, 0.2); // Sweep duration
      
      // Apply frequency sweep using exponential ramp
      oscillator.frequency.setValueAtTime(startFreq, time);
      oscillator.frequency.exponentialRampToValueAtTime(endFreq, time + sweepDuration);

      // Connect oscillator through envelope
      oscillator.connect(envelope.getGainNode());

      // Start oscillator
      oscillator.start(time);

      // Stop after duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      oscillator.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeOscillators.push(oscillator);

      // Clean up after oscillator stops
      oscillator.onended = () => {
        try {
          oscillator.disconnect();
        } catch (e) {
          // Ignore errors
        }
        activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      Object.assign({}, newParameters);
    },

    dispose(): void {
      activeOscillators.forEach(osc => {
        try { osc.stop(); osc.disconnect(); } catch (e) { /* ignore */ }
      });
      activeOscillators = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Create an explosion SFX voice
 * Explosion: noise + filter sweep + envelope with parameter automation
 */
function createExplosionVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Explosion has a short, punchy envelope with quick decay
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.3,
    sustain: 0.0,
    release: 0.2,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.7;
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeNoiseSources: AudioBufferSourceNode[] = [];
  let activeFilters: BiquadFilterNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null,

    trigger(_note: string, time: number, duration: number): void {
      // Explosion sound: noise with filter sweep from high to low

      // Create noise source
      const noiseBuffer = generateWhiteNoise(audioContext, 0.5);
      const noiseSource = audioContext.createBufferSource();
      noiseSource.buffer = noiseBuffer;

      // Create lowpass filter for sweep effect
      const filter = audioContext.createBiquadFilter();
      filter.type = 'lowpass';
      
      // Filter sweep parameters (high to low for explosion effect)
      const startFreq = 8000;  // Start with bright sound
      const endFreq = 100;     // End with deep rumble
      const sweepDuration = Math.min(duration, 0.4);
      
      // Apply filter frequency sweep
      filter.frequency.setValueAtTime(startFreq, time);
      filter.frequency.exponentialRampToValueAtTime(endFreq, time + sweepDuration);
      filter.Q.value = 1;

      // Connect: noise -> filter -> envelope
      noiseSource.connect(filter);
      filter.connect(envelope.getGainNode());

      // Start noise
      noiseSource.start(time);

      // Stop after duration + release
      const stopTime = time + duration + adsrSettings.release + 0.1;
      noiseSource.stop(stopTime);

      // Trigger envelope
      envelope.trigger(time, duration, adsrSettings);

      // Store for cleanup
      activeNoiseSources.push(noiseSource);
      activeFilters.push(filter);

      // Clean up after noise stops
      noiseSource.onended = () => {
        try {
          noiseSource.disconnect();
          filter.disconnect();
        } catch (e) {
          // Ignore errors
        }
        activeNoiseSources = activeNoiseSources.filter(src => src !== noiseSource);
        activeFilters = activeFilters.filter(f => f !== filter);
      };
    },

    updateParameters(newParameters: InstrumentParameters): void {
      Object.assign({}, newParameters);
    },

    dispose(): void {
      activeNoiseSources.forEach(src => {
        try { src.stop(); src.disconnect(); } catch (e) { /* ignore */ }
      });
      activeFilters.forEach(f => {
        try { f.disconnect(); } catch (e) { /* ignore */ }
      });
      
      activeNoiseSources = [];
      activeFilters = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Create a coin SFX voice
 * Coin: arpeggio of frequencies with parameter automation
 */
function createCoinVoice(config: VoiceConfig): Voice {
  const { audioContext, adsr, effectsNode, destination } = config;
  
  // Coin has a short, bright envelope
  const adsrSettings = adsr || {
    attack: 0.001,
    decay: 0.15,
    sustain: 0.0,
    release: 0.1,
  };

  // Create voice gain node
  const voiceGain = audioContext.createGain();
  voiceGain.gain.value = 0.5;
  
  // Connect to effects or destination
  if (effectsNode) {
    voiceGain.connect(effectsNode);
    effectsNode.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  // Create ADSR envelope
  const envelope = new ADSREnvelope(audioContext, voiceGain);

  // Store active nodes for cleanup
  let activeOscillators: OscillatorNode[] = [];

  const voice: Voice = {
    gainNode: voiceGain,
    effectsNode: effectsNode || null,
    filterNode: null,

    trigger(_note: string, time: number, duration: number): void {
      // Coin sound: quick arpeggio of frequencies (ascending)
      
      // Define arpeggio frequencies (major triad going up)
      const baseFreq = 800; // Base frequency
      const frequencies = [
        baseFreq,           // Root
        baseFreq * 1.25,    // Major third
        baseFreq * 1.5,     // Perfect fifth
        baseFreq * 2.0,     // Octave
      ];
      
      // Time between each note in the arpeggio
      const noteInterval = 0.04; // 40ms between notes
      
      frequencies.forEach((freq, index) => {
        const noteTime = time + (index * noteInterval);
        const noteDuration = 0.08; // Each note is short
        
        // Create oscillator for this note
        const oscillator = audioContext.createOscillator();
        oscillator.type = 'sine'; // Sine wave for clean, bell-like tone
        oscillator.frequency.value = freq;

        // Create individual gain for this note (fade out as we go up)
        const noteGain = audioContext.createGain();
        const amplitude = 1.0 - (index * 0.15); // Slightly decrease volume for higher notes
        noteGain.gain.setValueAtTime(amplitude, noteTime);
        noteGain.gain.exponentialRampToValueAtTime(0.01, noteTime + noteDuration);

        // Connect: oscillator -> noteGain -> envelope
        oscillator.connect(noteGain);
        noteGain.connect(envelope.getGainNode());

        // Start and stop oscillator
        oscillator.start(noteTime);
        oscillator.stop(noteTime + noteDuration);

        // Store for cleanup
        activeOscillators.push(oscillator);

        // Clean up after oscillator stops
        oscillator.onended = () => {
          try {
            oscillator.disconnect();
            noteGain.disconnect();
          } catch (e) {
            // Ignore errors
          }
          activeOscillators = activeOscillators.filter(osc => osc !== oscillator);
        };
      });

      // Trigger envelope for the overall sound
      envelope.trigger(time, duration, adsrSettings);
    },

    updateParameters(newParameters: InstrumentParameters): void {
      Object.assign({}, newParameters);
    },

    dispose(): void {
      activeOscillators.forEach(osc => {
        try { osc.stop(); osc.disconnect(); } catch (e) { /* ignore */ }
      });
      activeOscillators = [];

      envelope.dispose();
      try { voiceGain.disconnect(); } catch (e) { /* ignore */ }
    },
  };

  return voice;
}

/**
 * Factory function to create native Web Audio API voices
 * 
 * @param audioContext - Native AudioContext
 * @param instrumentTypes - Array of instrument types to create
 * @param adsrSettings - Optional array of ADSR settings per voice
 * @param instrumentParameters - Optional array of instrument parameters per voice
 * @param parametersEnabled - Optional array indicating if parameters are enabled per voice
 * @param effectsNodes - Optional array of AudioWorkletNodes for effects per voice
 * @param destination - Optional destination node (defaults to audioContext.destination)
 * @returns Array of Voice instances
 */
export function createNativeVoices(
  audioContext: AudioContext,
  instrumentTypes: InstrumentType[],
  adsrSettings?: ADSRSettings[],
  instrumentParameters?: (InstrumentParameters | undefined)[],
  _parametersEnabled?: boolean[],
  effectsNodes?: (AudioWorkletNode | null)[],
  destination?: AudioNode
): Voice[] {
  // Use provided destination or create master gain node as destination
  const masterGain = destination || audioContext.destination;

  const voices: Voice[] = [];

  for (let i = 0; i < instrumentTypes.length; i++) {
    const instrumentType = instrumentTypes[i];
    
    if (!instrumentType) {
      console.warn(`Missing instrument type at index ${i}, skipping`);
      continue;
    }
    
    const adsr = adsrSettings?.[i];
    const parameters = instrumentParameters?.[i];
    const effectsNode = effectsNodes?.[i] || null;

    const config: VoiceConfig = {
      audioContext,
      instrumentType,
      adsr,
      parameters,
      effectsNode,
      destination: masterGain,
    };

    // Create voices based on instrument type
    const basicOscillatorTypes = ['square', 'triangle', 'sawtooth', 'sine'];
    const pulseWaveTypes = ['pulse25', 'pulse50', 'pulse75'];
    const noiseTypes = ['noise-white', 'noise-pink', 'noise-brown'];
    const fmTypes = ['fmsynth'];
    const amTypes = ['amsynth'];
    const padTypes = ['pad-choir', 'pad-sweep', 'pad-drone'];
    const arpTypes = ['arp-fast', 'arp-slow', 'arp-random', 'arp-chord'];
    
    if (basicOscillatorTypes.includes(instrumentType)) {
      voices.push(createOscillatorVoice(config));
    } else if (pulseWaveTypes.includes(instrumentType)) {
      voices.push(createPulseVoice(config));
    } else if (noiseTypes.includes(instrumentType)) {
      voices.push(createNoiseVoice(config));
    } else if (fmTypes.includes(instrumentType)) {
      voices.push(createFMVoice(config));
    } else if (amTypes.includes(instrumentType)) {
      voices.push(createAMVoice(config));
    } else if (padTypes.includes(instrumentType)) {
      voices.push(createPadVoice(config));
    } else if (arpTypes.includes(instrumentType)) {
      voices.push(createArpVoice(config));
    } else if (instrumentType === 'drum-kick') {
      voices.push(createKickDrumVoice(config));
    } else if (instrumentType === 'drum-snare') {
      voices.push(createSnareDrumVoice(config));
    } else if (instrumentType === 'drum-hat') {
      voices.push(createHiHatVoice(config, false));
    } else if (instrumentType === 'drum-hat-open') {
      voices.push(createHiHatVoice(config, true));
    } else if (instrumentType === 'drum-tom' || instrumentType === 'drum-tom-mid') {
      voices.push(createTomDrumVoice(config, 'mid'));
    } else if (instrumentType === 'drum-tom-low') {
      voices.push(createTomDrumVoice(config, 'low'));
    } else if (instrumentType === 'drum-tom-high') {
      voices.push(createTomDrumVoice(config, 'high'));
    } else if (instrumentType === 'drum-cymbal-crash') {
      voices.push(createCymbalVoice(config, 'crash'));
    } else if (instrumentType === 'drum-cymbal-ride') {
      voices.push(createCymbalVoice(config, 'ride'));
    } else if (instrumentType === 'drum-clap') {
      voices.push(createClapVoice(config));
    } else if (instrumentType === 'pluck') {
      voices.push(createPluckVoice(config));
    } else if (instrumentType === 'metal') {
      voices.push(createMetalVoice(config));
    } else if (instrumentType === 'sfx-laser') {
      voices.push(createLaserVoice(config));
    } else if (instrumentType === 'sfx-explosion') {
      voices.push(createExplosionVoice(config));
    } else if (instrumentType === 'sfx-coin') {
      voices.push(createCoinVoice(config));
    } else {
      // Placeholder: create a triangle wave for unsupported types
      console.warn(`Instrument type '${instrumentType}' not yet implemented, using triangle wave`);
      const fallbackConfig: VoiceConfig = {
        audioContext,
        instrumentType: 'triangle',
        adsr,
        parameters,
        effectsNode,
        destination: masterGain,
      };
      voices.push(createOscillatorVoice(fallbackConfig));
    }
  }

  return voices;
}
